// frontend/src/screens/admin/sps/EmailCentreScreen.jsx
import { useState, useRef, useEffect } from 'react';

import {
  Container, Row, Col, Table, Button, Badge, Form, Alert, Spinner,
  Pagination, Card, ProgressBar, Modal,
} from 'react-bootstrap';
import {
  useGetStaffDirectoryQuery,
  useSendToOneMutation,
  useSendBatchMutation,
} from '../../../slices/spsApiSlices';


// ── API base URL — works in dev (port 5800) and production (same host) ──────
const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:5800'
  : '';

export default function EmailCentreScreen() {
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState(new Set());
  const [feedback, setFeedback] = useState(null);
  const [sending,  setSending]  = useState(false);
  const [progress, setProgress] = useState({ sent: 0, failed: 0, skipped: 0, total: 0 });
  const [showConfirm, setShowConfirm] = useState(false);

  //const queryParams = { page, limit: 50, ...(search && { search }) };
  // query params for the table
  
  const [domain, setDomain] = useState('');
  const [domains, setDomains] = useState([]);

useEffect(() => {
  fetch(`${API_BASE}/api/staff-directory/email-domains`, { credentials: 'include' })
    .then(r => r.json())
    .then(setDomains)
    .catch(() => {});
}, []);

// query params for the table
const queryParams = { page, limit: 50, ...(search && { search }), ...(domain && { domain }) };

// selectAllMatching — add domain to the params
const params = new URLSearchParams();
if (search) params.set('search', search);
if (domain) params.set('domain', domain);



  const { data, isLoading, isFetching, refetch } = useGetStaffDirectoryQuery(queryParams);

  

  const [sendOne]   = useSendToOneMutation();
  const [sendBatch] = useSendBatchMutation();

  const staff = data?.staff  || [];
  const pages = data?.pages  || 1;
  const total = data?.total  || 0;

  const toggleSelect = (pps) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(pps) ? next.delete(pps) : next.add(pps);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(staff.map((s) => s.ppsNumber)));
  const clearAll  = () => setSelected(new Set());


  const [sendStartedAt, setSendStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  const [sseLog, setSseLog] = useState([]);
  const [runMode, setRunMode] = useState('resume'); // 'resume' | 'fresh'
  const evsRef = useRef(null);

  const appendSseLog = (line, type = 'log') => {
    setSseLog((prev) => [...prev.slice(-300), { line, type }]);
  };

const stopSend = () => {
  if (evsRef.current) evsRef.current.close();
  appendSseLog('Cancelling — finishing the current send, then stopping...', 'log');
};

const handleSearchChange = (e) => {
  setSearch(e.target.value);
  setPage(1);
  clearAll();
};

const handleDomainChange = (e) => {
  setDomain(e.target.value);
  setPage(1);
  clearAll();
};


const [selectingAll, setSelectingAll] = useState(false);

const selectAllMatching = async () => {
  setSelectingAll(true);
  try {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (domain) params.set('domain', domain);  // ← this was missing
    const res = await fetch(`${API_BASE}/api/staff-directory/pps-numbers?${params}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    setSelected(new Set(data.ppsNumbers));
    setFeedback({
      type: 'success',
      msg: `Selected all ${data.ppsNumbers.length.toLocaleString()} matching staff`
        + `${domain ? ` at @${domain}` : ''} (with email + cert on file)`,
    });
  } catch (err) {
    setFeedback({ type: 'danger', msg: `Could not select all: ${err.message}` });
  } finally {
    setSelectingAll(false);
  }
};


useEffect(() => {
  if (!sending) return;
  const interval = setInterval(() => setNow(Date.now()), 1000);
  return () => clearInterval(interval);
}, [sending]);

  const handleSendSelected = () => {
  setShowConfirm(false);
  if (selected.size === 0) return;
  
  setSending(true);
  setFeedback(null);
  setSseLog([]);
  setSendStartedAt(Date.now());
  setProgress({ sent: 0, failed: 0, skipped: 0, total: selected.size });



  const controller = new AbortController();
  const ppsList = Array.from(selected);

  fetch(`${API_BASE}/api/email/send-batch-stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    signal: controller.signal,
    body: JSON.stringify({ ppsList, fresh: runMode === 'fresh' }),
  })
    .then(async (res) => {
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResults = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'progress') {
              setProgress((p) => ({ ...p, total: event.total }));
              appendSseLog(event.line, 'progress');
            } else if (event.type === 'error') {
              appendSseLog(event.line, 'error');
            } else if (event.type === 'warning') {
              appendSseLog(event.message, 'warning');
            } else if (event.type === 'log') {
              appendSseLog(event.line, 'log');
            } else if (event.type === 'start') {
              appendSseLog(event.message, 'start');
            } else if (event.type === 'done') {
              finalResults = event;
            }
          } catch (_) { /* ignore non-JSON */ }
        }
      }

      if (finalResults) {
        setFeedback({
          type: finalResults.failed === 0 ? 'success' : 'warning',
          msg: `Batch ${finalResults.status} — ${finalResults.sent} sent, ${finalResults.failed} failed, ${finalResults.skipped} skipped`,
        });
      }
    })
    .catch((err) => {
        if (err.name !== 'AbortError') {
          appendSseLog(`Stream interrupted: ${err.message} (check server logs)`, 'error');
          setFeedback({ type: 'danger', msg: 'Email batch stream was interrupted — check the log.' });
        }
        // AbortError from a deliberate Stop click is expected — no error shown.
        // The server's 'done' event with status:'cancelled' won't arrive in this
        // case since we closed the connection client-side; show a neutral message.
        if (err.name === 'AbortError') {
          setFeedback({ type: 'secondary', msg: 'Email batch stopped. Already-sent emails are recorded — choose "Resume" to continue with the rest.' });
        }
      })

    .finally(() => {
      setSending(false);
      setSendStartedAt(null);
      clearAll();
      refetch();
    });

  evsRef.current = { close: () => controller.abort() };
};

useEffect(() => () => { if (evsRef.current) evsRef.current.close(); }, []);

  const handleSendOne = async (pps) => {
    setFeedback(null);
    try {
      await sendOne(pps).unwrap();
      setFeedback({ type: 'success', msg: `✓ Email sent for ${pps}` });
      refetch();
    } catch (err) {
      setFeedback({ type: 'danger', msg: err?.data?.message || `Failed to send for ${pps}` });
    }
  };

  const processedCount = progress.sent + progress.failed + progress.skipped;
const elapsedMs = sendStartedAt ? now - sendStartedAt : 0;
const remainingCount = Math.max(progress.total - processedCount, 0);

// Use actual observed pace once we have a few data points (more accurate
// than the theoretical 2.2s, since it naturally accounts for throttle
// backoffs); fall back to the theoretical pace before that.
const msPerEmail = processedCount >= 3 && elapsedMs > 0
  ? elapsedMs / processedCount
  : 2200;
const etaMs = remainingCount * msPerEmail;

function formatDuration(ms) {
  if (ms <= 0) return '0s';
  const totalSeconds = Math.round(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}



  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Email Centre</h4>
          <small className="text-muted">
            Send Annual Benefit Statements and CPI summaries to staff by email
          </small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          {selected.size > 0 && (
            <Button variant="primary" onClick={() => setShowConfirm(true)} disabled={sending}>
              ✉ Send to {selected.size} selected
            </Button>
          )}
        </Col>
      </Row>

      {feedback && (
        <Alert variant={feedback.type} dismissible onClose={() => setFeedback(null)} className="mb-3">
          {feedback.msg}
        </Alert>
      )}

      {sending && (
        <Card className="mb-3">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small fw-semibold">
                  Sending… {processedCount.toLocaleString()} / {progress.total.toLocaleString()}
                  {remainingCount > 0 && (
                    <span className="text-muted ms-2">
                      · ~{formatDuration(etaMs)} remaining
                    </span>
                  )}
                </span>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={stopSend}
                  style={{ fontSize: '.72rem', padding: '2px 10px' }}
                >
                  ✕ Stop
                </Button>
              </div>
            <ProgressBar
                now={Math.round((processedCount / Math.max(progress.total, 1)) * 100)}
                label={`${progress.sent} sent`}
                animated
              />

            {sseLog.length > 0 && (
              <div style={{
                height: 240, overflowY: 'auto', background: '#f8f9fa',
                border: '1px solid #dee2e6', borderRadius: 4, padding: '8px 10px',
                fontFamily: 'monospace', fontSize: '.78rem', marginBottom: '1rem',
              }}>
                {sseLog.map((entry, i) => (
                  <div key={i} style={{
                    color: entry.type === 'error' ? '#dc3545'
                        : entry.type === 'warning' ? '#fd7e14'
                        : entry.type === 'start' ? '#198754'
                        : '#212529',
                  }}>
                    {entry.line}
                  </div>
                ))}
              </div>
            )}


          </Card.Body>
        </Card>
      )}

      {/* Filters and select */}
      <Row className="g-2 mb-2 align-items-center">
        <Col xs={12} sm={4} md={3}>
          <Form.Control
            size="sm"
            placeholder="Search name or PPS…"
            value={search}
            onChange={handleSearchChange}
          />
        </Col>

        <Col xs={12} sm={3} md={2}>
        <Form.Select size="sm" value={domain} onChange={handleDomainChange}>
          <option value="">All domains</option>
          {domains.map(d => (
            <option key={d.domain} value={d.domain}>@{d.domain} ({d.count})</option>
          ))}
        </Form.Select>
      </Col>


        <Col xs="auto" className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={selectAll}>Select page</Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={selectAllMatching}
            disabled={selectingAll}
          >
            {selectingAll
              ? <><Spinner size="sm" animation="border" className="me-1" /> Selecting…</>
              : `Select all ${total.toLocaleString()}`}
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={clearAll} disabled={selected.size === 0}>Clear ({selected.size})</Button>
        </Col>
        <Col className="text-muted small text-end">
        {total.toLocaleString()} staff · {selected.size.toLocaleString()} selected
        {selected.size > 0 && selected.size !== total && page === 1 && pages === 1 && (
          <span className="text-warning ms-2">
            ⚠ selection may not match current filter — reselect to be sure
          </span>
        )}
      </Col>
      </Row>

      <div className="table-responsive" style={{ position: 'relative' }}>
        {(isLoading || isFetching) && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.7)', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner animation="border" />
          </div>
        )}
        <Table bordered hover size="sm" className="mb-0" style={{ fontSize: '.81rem' }}>
          <thead className="table-dark">
            <tr>
              <th style={{ width: 36 }}>
                <Form.Check
                  checked={staff.length > 0 && staff.every((s) => selected.has(s.ppsNumber))}
                  onChange={(e) => e.target.checked ? selectAll() : clearAll()}
                />
              </th>
              <th>PPS</th>
              <th>Name</th>
              <th>Email</th>
              <th>Last cert</th>
              <th>Last email sent</th>
              <th style={{ width: 80 }}>Send</th>
            </tr>
          </thead>
          <tbody>
  {staff.map((m) => {
    const optedOut = m.sendEmail === false;
    const canSend  = m.email && m.lastStatementYear && !optedOut;
    return (
      <tr key={m.ppsNumber} style={{ background: selected.has(m.ppsNumber) ? '#f0f5ff' : undefined }}>
        <td>
          <Form.Check
            checked={selected.has(m.ppsNumber)}
            onChange={() => toggleSelect(m.ppsNumber)}
            disabled={!canSend}
          />
        </td>
        <td style={{ fontFamily: 'monospace', fontSize: '.75rem' }}>{m.ppsNumber}</td>
        <td>{m.employeeName}</td>
        <td style={{ fontSize: '.75rem' }}>
          {m.email || <span className="text-danger fst-italic">no email</span>}
        </td>
        <td className="text-center">
          {m.lastStatementYear
            ? <Badge bg="success" style={{ fontSize: '.65rem' }}>{m.lastStatementYear}</Badge>
            : <span className="text-muted small">—</span>}
        </td>
        <td>
          {m.lastEmailSent
            ? <span className="small">{new Date(m.lastEmailSent).toLocaleDateString('en-IE')}</span>
            : <span className="text-muted small">Not sent</span>}
          {m.lastEmailStatus === 'failed' && <Badge bg="danger" className="ms-1" style={{ fontSize: '.62rem' }}>failed</Badge>}
          {optedOut && <Badge bg="secondary" className="ms-1" style={{ fontSize: '.62rem' }}>opted out</Badge>}
        </td>
        <td>
          <Button
            size="sm" variant="outline-primary"
            style={{ fontSize: '.7rem', padding: '1px 6px' }}
            onClick={() => handleSendOne(m.ppsNumber)}
            disabled={sending || !canSend}
            title={
              optedOut ? 'This staff member has opted out of email'
              : !m.email ? 'No email on file'
              : !m.lastStatementYear ? 'No cert generated yet'
              : ''
            }
          >
            Send
          </Button>
        </td>
      </tr>
    );
  })}
</tbody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination size="sm">
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
            {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
              <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
            ))}
            <Pagination.Next disabled={page === pages} onClick={() => setPage(page + 1)} />
          </Pagination>
        </div>
      )}

      {/* Confirm bulk send modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Bulk Email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>You are about to send Annual Benefit Statements to <strong>{selected.size}</strong> staff members.</p>

        <Form.Check
          type="radio" name="emailRunMode" id="email-mode-resume"
          label="Skip anyone already successfully emailed this year (recommended)"
          checked={runMode === 'resume'} onChange={() => setRunMode('resume')}
          className="mb-2"
        />
        <Form.Check
          type="radio" name="emailRunMode" id="email-mode-fresh"
          label="Re-send to everyone selected, even if already sent"
          checked={runMode === 'fresh'} onChange={() => setRunMode('fresh')}
          className="mb-3"
        />

        <Alert variant="warning" className="mb-0">
          Office 365 limits sending to ~30/minute, so this will take roughly{' '}
          <strong>{Math.ceil(selected.size * 2.2 / 60)} minutes</strong>. Keep this tab open until it finishes —
          you can safely navigate away and come back; an interrupted run can be resumed.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
        <Button variant="primary" onClick={handleSendSelected}>Send {selected.size} emails</Button>
        
      </Modal.Footer>
    </Modal>



    </Container>
  );
}